import React, { useCallback, useEffect, useMemo } from 'react'
import { generatePath, useLocation, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import { Tooltip } from 'antd'
import styled from 'styled-components'
import * as routes from 'portal/routes'
import PropTypes from 'prop-types'

import { UpgradeToPro } from './UpgradeToPro'

import { ALERT_TYPES } from 'portal/lib/constants/alertTypes'
import { useResponsive } from 'portal/lib/hooks'
import { SideBar, icons } from 'portal/lib/primitives'

import { projectsGetSaga } from 'portal/store/projects/actions'
import { projectGetDetailsSaga } from 'portal/store/projectDetails/actions'
import { uiSetSidebarCollapsed } from 'portal/store/ui/actions'

import { BIReportSideBarButton } from './BIReportSideBarButton'

import { ProjectSettingsSidebarButton } from './ProjectSettingsSidebarButton'
import { AccountSettingsSidebarButton } from './AccountSettingsSidebarButton'
import { useMixpanelEvents } from './Mixpanel'

export function Sidebar() {
  const { accountId, projectId } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()
  const { isMobile } = useResponsive()

  const lastUsedProjectId = sessionStorage.getItem('last-used-project-id')

  const { projects, isLoading } = useSelector(state => state.projects)
  const projectDetails = useSelector(state => state.projectDetails.modules)
  const isCollapsed = useSelector(state => state.ui.data.sidebarCollapsed)

  const enabledModules = useMemo(() => {
    const tempObj = {}
    if (projectDetails && projectDetails.moduleTags) {
      projectDetails.moduleTags.forEach(element => {
        tempObj[element.moduleTagDefinitionName.toLowerCase()] =
          element.isTagged
      })
    }
    return tempObj
  }, [projectDetails])

  const {
    trackAlertsMaterialCertificationsPageEventClick,
    trackAlertsQualityAssurancePageEventClick,
    trackAlertsSubmissionIssuesPageEventClick,
    trackAlertsWasteCompliancePageEventClick,
    trackDashboardEventClick,
    trackDashboardProEventClick,
    trackEventsPageEventClick
  } = useMixpanelEvents()

  // If current page has a projectId save in session storage
  //  in case we need to reference from a page that does not have this, e.g.Dashboard
  useEffect(() => {
    if (
      projectId &&
      lastUsedProjectId !== projectId &&
      projectId !== ':projectId'
    ) {
      sessionStorage.setItem('last-used-project-id', projectId)
    }
  }, [lastUsedProjectId, projectId])

  useEffect(() => {
    if (!projects && !isLoading) {
      dispatch(projectsGetSaga(accountId))
    }
  }, [projects, isLoading, dispatch, accountId])

  // Check if we need to refresh project details
  const getRefreshedProjectDetails = useMemo(() => {
    return (
      !projectDetails ||
      (projectId && projectDetails.projectId !== projectId) ||
      (!projectId && projectDetails.projectId !== lastUsedProjectId)
    )
  }, [lastUsedProjectId, projectDetails, projectId])

  useEffect(() => {
    if (getRefreshedProjectDetails) {
      if (accountId && projectId) {
        dispatch(projectGetDetailsSaga(accountId, projectId))
      } else if (accountId && lastUsedProjectId) {
        dispatch(projectGetDetailsSaga(accountId, lastUsedProjectId))
      } else if (accountId && projects && projects.length > 0) {
        // Default to first project
        dispatch(projectGetDetailsSaga(accountId, projects[0].projectId))
      }
    }
  }, [
    accountId,
    dispatch,
    getRefreshedProjectDetails,
    lastUsedProjectId,
    projectId,
    projects
  ])

  const createUri = useCallback(
    route => {
      if (projects && projects.length > 0 && !isLoading) {
        return generatePath(route, {
          accountId,
          projectId: projectId
            ? projectId
            : lastUsedProjectId
              ? lastUsedProjectId
              : projects[0].projectId
        })
      }
      return ''
    },
    [accountId, isLoading, lastUsedProjectId, projectId, projects]
  )

  const createAccountSettingsUri = useCallback(
    route => {
      if (accountId) {
        return generatePath(route, {
          accountId
        })
      }
      return ''
    },
    [accountId]
  )

  const recordsUriDelivery = createUri(routes.DELIVERY_DEFAULT)
  const eventsUriDelivery = createUri(routes.DELIVERY_EVENTS)

  const recordsUriWaste = createUri(routes.WASTE_DEFAULT)
  const eventsUriWaste = createUri(routes.WASTE_EVENTS)

  const utilitiesReadings = createUri(routes.UTILITIES_READINGS, null, null)
  const utilitiesMeters = createUri(routes.UTILITIES_METERS, null, null)

  const alertsMaterialCertificationsTable = createUri(
    routes.ALERTS_MATERIAL_CERTIFICATIONS_TABLE,
    null,
    null
  )
  const alertsQualityAssuranceTable = createUri(
    routes.ALERTS_QUALITY_ASSURANCE_TABLE,
    null,
    null
  )
  const alertsSubmissionErrorsTable = createUri(
    routes.ALERTS_SUBMISSION_ISSUES_TABLE,
    null,
    null
  )
  const alertsWasteComplianceTable = createUri(
    routes.ALERTS_WASTE_COMPLIANCE_TABLE,
    null,
    null
  )

  const projectSettingsUri = createUri(routes.PROJECTSETTINGS, null, null)

  const accountSettingsUri = createAccountSettingsUri(routes.ACCOUNT_SETTINGS)

  const onDashboardClick = useCallback(
    e => {
      e.stopPropagation()
      trackDashboardEventClick(projectId, accountId)
    },
    [projectId, accountId, trackDashboardEventClick]
  )

  const onDashboardProClick = useCallback(
    e => {
      e.stopPropagation()

      trackDashboardProEventClick(projectId, accountId)
    },
    [projectId, accountId, trackDashboardProEventClick]
  )

  const onEventsPageClick = useCallback(
    e => {
      e.stopPropagation()
      trackEventsPageEventClick(projectId, accountId)
    },
    [projectId, accountId, trackEventsPageEventClick]
  )

  const onAlertsMaterialCertificationsPageClick = useCallback(
    e => {
      e.stopPropagation()
      trackAlertsMaterialCertificationsPageEventClick(projectId, accountId)
    },
    [trackAlertsMaterialCertificationsPageEventClick, projectId, accountId]
  )

  const onAlertsQualityAssurancePageClick = useCallback(
    e => {
      e.stopPropagation()
      trackAlertsQualityAssurancePageEventClick(projectId, accountId)
    },
    [trackAlertsQualityAssurancePageEventClick, projectId, accountId]
  )

  const onAlertsSubmissionIssuesPageClick = useCallback(
    e => {
      e.stopPropagation()
      trackAlertsSubmissionIssuesPageEventClick(projectId, accountId)
    },
    [trackAlertsSubmissionIssuesPageEventClick, projectId, accountId]
  )

  const onAlertsWasteCompliancePageClick = useCallback(
    e => {
      e.stopPropagation()
      trackAlertsWasteCompliancePageEventClick(projectId, accountId)
    },
    [trackAlertsWasteCompliancePageEventClick, projectId, accountId]
  )

  return (
    <SideBar.Show>
      <SideBar.TopSection>
        {!isMobile && (
          <SideBar.SideBarGroup
            groupName="Dashboards"
            groupIcon={<icons.Dashboard />}
            active={location.pathname.includes('bireport')}
          >
            <BIReportSideBarButton
              reportName="Standard"
              reportType="Dashboard"
              onClick={onDashboardClick}
            />
            <BIReportSideBarButton
              reportName="Pro"
              reportType="Professional"
              onClick={onDashboardProClick}
            />
          </SideBar.SideBarGroup>
        )}
        {projectDetails && (
          <>
            <SideBar.SideBarGroup
              groupName="Deliveries"
              groupIcon={<icons.Modules.Delivery />}
              active={location.pathname.includes('delivery')}
              disabled={!enabledModules.delivery}
            >
              <SideBar.NavButton
                disabled={recordsUriDelivery === ''}
                id="Table"
                to={recordsUriDelivery}
              >
                Records
              </SideBar.NavButton>
              <SideBar.NavButton
                disabled={eventsUriDelivery === ''}
                id="Events"
                to={eventsUriDelivery}
                onClick={onEventsPageClick}
              >
                Events
              </SideBar.NavButton>
            </SideBar.SideBarGroup>

            <SideBar.SideBarGroup
              groupName="Waste"
              groupIcon={<icons.Modules.Waste />}
              active={location.pathname.includes('waste/')}
              disabled={!enabledModules.waste}
            >
              <SideBar.NavButton
                disabled={recordsUriWaste === ''}
                id="Table"
                to={recordsUriWaste}
              >
                Records
              </SideBar.NavButton>
              <SideBar.NavButton
                disabled={eventsUriWaste === ''}
                id="Events"
                to={eventsUriWaste}
                onClick={onEventsPageClick}
              >
                Events
              </SideBar.NavButton>
            </SideBar.SideBarGroup>

            <SideBar.SideBarGroup
              groupName="Utilities"
              groupIcon={<icons.Modules.Electricity />}
              active={location.pathname.includes('utilities')}
              disabled={!projectDetails.isProLicence}
              UpgradeToProComponent={UpgradeToPro}
            >
              <SideBar.NavButton
                disabled={utilitiesReadings === ''}
                to={utilitiesReadings}
              >
                Readings
              </SideBar.NavButton>
              <SideBar.NavButton
                disabled={utilitiesMeters === ''}
                to={utilitiesMeters}
              >
                Meters
              </SideBar.NavButton>
            </SideBar.SideBarGroup>

            <SideBar.SideBarGroup
              groupName="Alerts"
              groupIcon={<icons.ShieldTick colour="currentColor" />}
              active={location.pathname.includes('/alerts/')}
              UpgradeToProComponent={UpgradeToPro}
            >
              <SideBar.NavButton
                id="WasteComplianceAlerts"
                onClick={onAlertsWasteCompliancePageClick}
                to={alertsWasteComplianceTable}
              >
                {ALERT_TYPES.WASTE_COMPLIANCE}
              </SideBar.NavButton>
              <SideBar.NavButton
                id="MaterialCertificationsAlerts"
                onClick={onAlertsMaterialCertificationsPageClick}
                to={alertsMaterialCertificationsTable}
              >
                {ALERT_TYPES.MATERIAL_CERTIFICATIONS}
              </SideBar.NavButton>
              <SideBar.NavButton
                id="QualityAssuranceAlerts"
                onClick={onAlertsQualityAssurancePageClick}
                to={alertsQualityAssuranceTable}
                disabled={!projectDetails.isAlertsEnabled}
                UpgradeToProComponent={UpgradeToPro}
              >
                {ALERT_TYPES.QUALITY_ASSURANCE}
              </SideBar.NavButton>
              <SideBar.NavButton
                id="SubmissionErrorsAlerts"
                onClick={onAlertsSubmissionIssuesPageClick}
                to={alertsSubmissionErrorsTable}
                disabled={!projectDetails.isAlertsEnabled}
                UpgradeToProComponent={UpgradeToPro}
              >
                {ALERT_TYPES.SUBMISSION_ISSUES}
              </SideBar.NavButton>
            </SideBar.SideBarGroup>
          </>
        )}
      </SideBar.TopSection>

      <SideBar.BottomSection isCollapsed={isCollapsed}>
        <ProjectSettingsSidebarButton
          accountId={accountId}
          projectId={projectId}
          projectsTable={projectSettingsUri}
        />
        <AccountSettingsSidebarButton
          accountId={accountId}
          accountsTable={accountSettingsUri}
        />
      </SideBar.BottomSection>
      {!isMobile && (
        <Tooltip
          title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
          placement="right"
        >
          <ExpandCollapseButton
            onClick={() => dispatch(uiSetSidebarCollapsed(!isCollapsed))}
            isCollapsed={isCollapsed}
          >
            <icons.Collapse rotate={isCollapsed ? 180 : 0} />
          </ExpandCollapseButton>
        </Tooltip>
      )}
    </SideBar.Show>
  )
}

SideBar.propTypes = {
  dateDisabled: PropTypes.bool
}

const ExpandCollapseButton = styled.div`
  position: absolute;
  bottom: 20px;
  padding: 4px;
  right: ${({ isCollapsed }) => (isCollapsed ? '15px' : '1.125rem')};
  color: #ffffff;
  cursor: pointer;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`
